import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { Field, TextInput, TextArea, PageHeader, Checkbox, NumberInput } from "./formKit";
import { Trash2, Plus, Save, Upload, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_PRACT = { name: "", title: "", designation: "", qualifications: "", registration_number: "", bio: "", areas: "", active: true, display_order: 100 };
const EMPTY_SVC = { title: "", description: "", age_group: "", practitioner_ids: [], active: true, display_order: 100 };

export default function AdminTherapyPage() {
  const [settings, setSettings] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newPract, setNewPract] = useState(null);
  const [newSvc, setNewSvc] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    try {
      const [s, p, sv] = await Promise.all([
        adminApi.get("/admin/therapy/settings"),
        adminApi.get("/admin/therapy/practitioners"),
        adminApi.get("/admin/therapy/services"),
      ]);
      setSettings(s.data);
      setPractitioners(p.data.items || []);
      setServices(sv.data.items || []);
    } catch {
      toast.error("Couldn't load therapy settings.");
    }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/therapy/settings", settings);
      setSettings(data);
      toast.success("Therapy booking settings saved.");
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save settings — check URLs.");
    } finally {
      setSaving(false);
    }
  };

  const practFd = (p, fileKey) => {
    const fd = new FormData();
    ["name", "title", "designation", "qualifications", "registration_number", "bio"].forEach((k) => fd.append(k, p[k] || ""));
    fd.append("areas", typeof p.areas === "string" ? p.areas : (p.areas || []).join("|"));
    fd.append("active", String(!!p.active));
    fd.append("display_order", String(p.display_order ?? 100));
    const f = fileRefs.current[fileKey]?.files?.[0];
    if (f) fd.append("file", f);
    return fd;
  };

  const savePract = async (p, idx) => {
    if (!(p.name || "").trim()) { toast.error("Practitioner name is required."); return; }
    try {
      if (p.id) await adminApi.put(`/admin/therapy/practitioners/${p.id}`, practFd(p, p.id), { headers: { "Content-Type": "multipart/form-data" } });
      else await adminApi.post("/admin/therapy/practitioners", practFd(p, "new"), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Practitioner saved.");
      setNewPract(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save practitioner.");
    }
  };

  const deletePract = async (id) => {
    if (!window.confirm("Delete this practitioner profile?")) return;
    try { await adminApi.delete(`/admin/therapy/practitioners/${id}`); toast.success("Deleted."); await load(); }
    catch { toast.error("Couldn't delete."); }
  };

  const saveSvc = async (s) => {
    if (!(s.title || "").trim()) { toast.error("Service title is required."); return; }
    const body = { ...s, practitioner_ids: s.practitioner_ids || [] };
    try {
      if (s.id) await adminApi.put(`/admin/therapy/services/${s.id}`, body);
      else await adminApi.post("/admin/therapy/services", body);
      toast.success("Service saved.");
      setNewSvc(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save service.");
    }
  };

  const deleteSvc = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await adminApi.delete(`/admin/therapy/services/${id}`); toast.success("Deleted."); await load(); }
    catch { toast.error("Couldn't delete."); }
  };

  if (!settings) return <p className="text-sm text-slate-500 p-8">Loading…</p>;

  const resolved = (sid, pid) => {
    if (!settings.enabled) return `Fallback → "${settings.fallback_label}" (${settings.fallback_path})`;
    const url = (sid && settings.service_urls?.[sid]) || (pid && settings.practitioner_urls?.[pid]) || settings.main_url;
    return url ? `Jane → ${url}` : `Fallback → "${settings.fallback_label}" (${settings.fallback_path})`;
  };

  const practEditor = (p, setP, fileKey, onSave, onCancel, onDelete) => (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid gap-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Name *"><TextInput value={p.name} onChange={(v) => setP({ ...p, name: v })} data-testid="pract-name-input" /></Field>
        <Field label="Professional title"><TextInput value={p.title} onChange={(v) => setP({ ...p, title: v })} /></Field>
        <Field label="Designation"><TextInput value={p.designation} onChange={(v) => setP({ ...p, designation: v })} /></Field>
        <Field label="Registration / membership #" hint="Only if approved for publication"><TextInput value={p.registration_number} onChange={(v) => setP({ ...p, registration_number: v })} /></Field>
      </div>
      <Field label="Qualifications"><TextInput value={p.qualifications} onChange={(v) => setP({ ...p, qualifications: v })} /></Field>
      <Field label="Biography"><TextArea value={p.bio} onChange={(v) => setP({ ...p, bio: v })} rows={4} /></Field>
      <Field label="Areas of support" hint="Separate with | e.g. Emotional Regulation|Social Skills">
        <TextInput value={typeof p.areas === "string" ? p.areas : (p.areas || []).join("|")} onChange={(v) => setP({ ...p, areas: v })} />
      </Field>
      <div className="flex flex-wrap items-center gap-4">
        <Checkbox value={!!p.active} onChange={(v) => setP({ ...p, active: v })} label="Published (active)" />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-600">Order</span>
          <div className="w-20"><NumberInput value={p.display_order ?? 100} onChange={(v) => setP({ ...p, display_order: v })} /></div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
          <Upload className="h-3.5 w-3.5" /> Photo
          <input ref={(el) => (fileRefs.current[fileKey] = el)} type="file" accept=".jpg,.jpeg,.png,.webp" className="text-xs" data-testid="pract-photo-input" />
        </label>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white" data-testid="pract-save-btn">
          <Save className="h-3.5 w-3.5" /> Save
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600">Cancel</button>}
        {onDelete && (
          <button type="button" onClick={onDelete} className="ml-auto text-slate-400 hover:text-red-600" aria-label="Delete practitioner" data-testid="pract-delete-btn">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="grid gap-6" data-testid="admin-therapy-page">
      <PageHeader
        title="Therapy Booking Settings"
        subtitle="Manage Jane.app booking links, services, and practitioner profiles. Availability, pricing, intake, consent and payments are all managed in Jane — this page only controls what parents see and where booking buttons lead."
      />

      {/* ---- Jane booking settings ---- */}
      <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4" data-testid="therapy-settings-section">
        <h3 className="font-display text-lg font-bold tracking-tight">Jane Online Booking</h3>
        <div className="flex items-center gap-3">
          <Checkbox value={!!settings.enabled} onChange={(v) => setSettings({ ...settings, enabled: v })} label="Enable Jane Online Booking" data-testid="therapy-enable-toggle" />
          <span className={`pill text-[10px] font-bold ${settings.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {settings.enabled ? "ON — booking buttons link to Jane" : "OFF — all buttons show the fallback"}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Main Jane Booking URL" hint="https:// link on janeapp.com / jane.app only">
            <TextInput value={settings.main_url || ""} onChange={(v) => setSettings({ ...settings, main_url: v })} placeholder="https://yourclinic.janeapp.com" data-testid="therapy-main-url-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Booking button label"><TextInput value={settings.button_label || ""} onChange={(v) => setSettings({ ...settings, button_label: v })} data-testid="therapy-button-label-input" /></Field>
            <Field label="Fallback button label"><TextInput value={settings.fallback_label || ""} onChange={(v) => setSettings({ ...settings, fallback_label: v })} data-testid="therapy-fallback-label-input" /></Field>
          </div>
        </div>
        <p className="text-xs text-slate-500">Fallback destination: <span className="font-semibold">{settings.fallback_path}</span> (existing contact/inquiry page).</p>

        {(practitioners.length > 0 || services.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4">
            {practitioners.length > 0 && (
              <div className="rounded-2xl border border-slate-200 p-4 grid gap-2.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Practitioner booking URLs (optional)</p>
                {practitioners.map((p) => (
                  <Field key={p.id} label={p.name}>
                    <TextInput
                      value={settings.practitioner_urls?.[p.id] || ""}
                      onChange={(v) => setSettings({ ...settings, practitioner_urls: { ...(settings.practitioner_urls || {}), [p.id]: v } })}
                      placeholder="https://…janeapp.com/#/staff_member/…"
                      data-testid={`therapy-pract-url-${p.id}`}
                    />
                  </Field>
                ))}
              </div>
            )}
            {services.length > 0 && (
              <div className="rounded-2xl border border-slate-200 p-4 grid gap-2.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Service booking URLs (optional)</p>
                {services.map((s) => (
                  <Field key={s.id} label={s.title}>
                    <TextInput
                      value={settings.service_urls?.[s.id] || ""}
                      onChange={(v) => setSettings({ ...settings, service_urls: { ...(settings.service_urls || {}), [s.id]: v } })}
                      placeholder="https://…janeapp.com/#/treatment/…"
                      data-testid={`therapy-service-url-${s.id}`}
                    />
                  </Field>
                ))}
              </div>
            )}
          </div>
        )}

        <button type="button" onClick={saveSettings} disabled={saving}
          className="inline-flex w-fit items-center gap-2 justify-self-start rounded-full bg-[#FF8C42] px-5 py-2.5 text-xs font-bold text-white disabled:opacity-50"
          data-testid="therapy-settings-save-btn">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save booking settings"}
        </button>

        {/* Behaviour preview */}
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 grid gap-1.5" data-testid="therapy-booking-preview">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><ExternalLink className="h-3 w-3" /> Current public button behaviour</p>
          <p className="text-xs text-slate-600">General page button: <span className="font-semibold">{resolved(null, null)}</span></p>
          {practitioners.slice(0, 4).map((p) => (
            <p key={p.id} className="text-xs text-slate-600">{p.name}: <span className="font-semibold">{resolved(null, p.id)}</span></p>
          ))}
          {services.slice(0, 4).map((s) => (
            <p key={s.id} className="text-xs text-slate-600">{s.title}: <span className="font-semibold">{resolved(s.id, null)}</span></p>
          ))}
        </div>
      </section>

      {/* ---- Services ---- */}
      <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4" data-testid="therapy-services-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold tracking-tight">Therapy Services (public descriptions)</h3>
          {!newSvc && (
            <button type="button" onClick={() => setNewSvc({ ...EMPTY_SVC })} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700" data-testid="therapy-add-service-btn">
              <Plus className="h-3.5 w-3.5" /> Add service
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 -mt-2">Marketing content only — operational pricing, durations and availability are managed in Jane.</p>
        {newSvc && svcEditor(newSvc, setNewSvc, () => saveSvc(newSvc), () => setNewSvc(null), null, practitioners)}
        {services.length === 0 && !newSvc && <p className="text-sm text-slate-500" data-testid="therapy-services-empty">No services yet.</p>}
        {services.map((s, i) => (
          <SvcRow key={s.id} svc={s} practitioners={practitioners} onSave={saveSvc} onDelete={() => deleteSvc(s.id)} idx={i} />
        ))}
      </section>

      {/* ---- Practitioners ---- */}
      <section className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-4" data-testid="therapy-practitioners-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold tracking-tight">Practitioners</h3>
          {!newPract && (
            <button type="button" onClick={() => setNewPract({ ...EMPTY_PRACT })} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700" data-testid="therapy-add-pract-btn">
              <Plus className="h-3.5 w-3.5" /> Add practitioner
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 -mt-2">Only publish approved information — never invented credentials. Biography content is separate from booking links above.</p>
        {newPract && practEditor(newPract, setNewPract, "new", () => savePract(newPract), () => setNewPract(null), null)}
        {practitioners.length === 0 && !newPract && <p className="text-sm text-slate-500" data-testid="therapy-practs-empty">No practitioners yet — the public page shows a friendly "profiles coming soon" note.</p>}
        {practitioners.map((p, i) => (
          <PractRow key={p.id} pract={p} editor={practEditor} onSave={savePract} onDelete={() => deletePract(p.id)} idx={i} />
        ))}
      </section>
    </div>
  );
}

function svcEditor(s, setS, onSave, onCancel, onDelete, practitioners) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid gap-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Service title *"><TextInput value={s.title} onChange={(v) => setS({ ...s, title: v })} data-testid="svc-title-input" /></Field>
        <Field label="Age group (optional)"><TextInput value={s.age_group} onChange={(v) => setS({ ...s, age_group: v })} placeholder="Ages 3–12" /></Field>
      </div>
      <Field label="Short public description"><TextArea value={s.description} onChange={(v) => setS({ ...s, description: v })} rows={3} /></Field>
      {practitioners.length > 0 && (
        <Field label="Relevant practitioners">
          <div className="flex flex-wrap gap-2">
            {practitioners.map((p) => {
              const on = (s.practitioner_ids || []).includes(p.id);
              return (
                <button key={p.id} type="button"
                  onClick={() => setS({ ...s, practitioner_ids: on ? s.practitioner_ids.filter((x) => x !== p.id) : [...(s.practitioner_ids || []), p.id] })}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold border ${on ? "bg-[#123B6D] border-[#123B6D] text-white" : "bg-white border-slate-300 text-slate-600"}`}>
                  {p.name}
                </button>
              );
            })}
          </div>
        </Field>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <Checkbox value={!!s.active} onChange={(v) => setS({ ...s, active: v })} label="Published (active)" />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-600">Order</span>
          <div className="w-20"><NumberInput value={s.display_order ?? 100} onChange={(v) => setS({ ...s, display_order: v })} /></div>
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white" data-testid="svc-save-btn">
          <Save className="h-3.5 w-3.5" /> Save
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600">Cancel</button>}
        {onDelete && (
          <button type="button" onClick={onDelete} className="ml-auto text-slate-400 hover:text-red-600" aria-label="Delete service" data-testid="svc-delete-btn">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function SvcRow({ svc, practitioners, onSave, onDelete, idx }) {
  const [s, setS] = useState(svc);
  useEffect(() => setS(svc), [svc]);
  return <div data-testid={`therapy-service-row-${idx}`}>{svcEditor(s, setS, () => onSave(s), null, onDelete, practitioners)}</div>;
}

function PractRow({ pract, editor, onSave, onDelete, idx }) {
  const [p, setP] = useState(pract);
  useEffect(() => setP(pract), [pract]);
  return (
    <div data-testid={`therapy-pract-row-${idx}`} className="grid gap-2">
      {p.photo_url && (
        <img src={`${BACKEND_URL}${p.photo_url}`} alt={p.name} className="h-16 w-16 rounded-xl object-cover border border-slate-200" data-testid={`pract-photo-preview-${idx}`} />
      )}
      {editor(p, setP, p.id, () => onSave(p), null, onDelete)}
    </div>
  );
}
